import { Stack, Text, Switch, Divider, Select } from '@mantine/core';
import { IconPlayerPlay } from '@tabler/icons-react';
import { WidgetNodeShell } from '../../components/flow-editor/nodes/widget-node-shell';
import { LabeledHandle } from '../../components/flow-editor/nodes/labeled-handle';
import type { WidgetDefinition, WidgetNodeProps, ConfigPanelProps } from '../types';
import { useBusinessHours } from '../business-hours';
import { startWidgetCore, type StartConfig } from '@legacy-shared/widgets/core/start';
import { getStartOutputs } from './start-outputs';
import { handleOffsets } from '../../lib/flow-editor/handle-offsets';

export { type StartConfig } from '@legacy-shared/widgets/core/start';

function StartNode({ id, data, selected }: WidgetNodeProps<StartConfig>) {
  const outputs = getStartOutputs(data.config);
  const offsets = handleOffsets(outputs);

  return (
    <WidgetNodeShell
      icon={IconPlayerPlay}
      title={data.label || 'Start'}
      subtitle="Call begins here"
      configured={true}
      errors={data.errors}
      selected={selected}
      color="green"
    >
      {outputs.map((o) => (
        <LabeledHandle
          key={o.id}
          type="source"
          position={o.position}
          id={o.id}
          label={o.label}
          offset={offsets.get(o.id)}
          nodeId={id}
        />
      ))}
    </WidgetNodeShell>
  );
}

function StartConfigPanel({ config, onChange }: ConfigPanelProps<StartConfig>) {
  const schedules = useBusinessHours();

  const scheduleOptions = (schedules ?? []).map((s) => ({
    value: s.id,
    label: s.name,
  }));

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        This is the entry point for incoming calls. Connect it to the first action in your flow.
      </Text>

      <Divider label="Business Hours" labelPosition="left" />

      <Select
        label="Business Hours Schedule"
        description="When set, calls outside business hours route to the Office Closed output."
        placeholder="None (always open)"
        data={scheduleOptions}
        value={config.businessHoursId ?? null}
        onChange={(v) => onChange({ ...config, businessHoursId: v || undefined })}
        searchable
        clearable
      />

      <Divider label="CRM Integration" labelPosition="left" />

      <Switch
        label="Enable Customer Caller ID Match"
        description="Look up caller ID in CRM on call start. Creates Match/No Match outputs."
        checked={config.enableCallerIdMatch ?? false}
        onChange={(e) => onChange({ ...config, enableCallerIdMatch: e.target.checked })}
      />

      <Divider label="Recording" labelPosition="left" />

      <Switch
        label="Enable Call Recording"
        description="Automatically start recording when the call begins. Recording can also be controlled mid-call with the Recording widget."
        checked={config.enableRecording ?? false}
        onChange={(e) => onChange({ ...config, enableRecording: e.target.checked })}
      />

      <Divider label="Additional Options" labelPosition="left" />

      <Switch
        label="Enable Zero Pressed"
        description="Create an output for when caller presses zero on any input widget. Useful for operator assistance."
        checked={config.enableZeroPressed ?? false}
        onChange={(e) => onChange({ ...config, enableZeroPressed: e.target.checked })}
      />
    </Stack>
  );
}

export const startWidget: WidgetDefinition<StartConfig> = {
  ...startWidgetCore,
  icon: 'IconPlayerPlay',
  variables: startWidgetCore.variables,

  handles: {
    inputs: [],
    outputs: [],
  },

  getDynamicOutputs: getStartOutputs,

  connectionRules: {
    allowedInputFrom: [],
    allowedOutputTo: ['*'],
  },

  ui: {
    component: StartNode,
    configPanel: StartConfigPanel,
    preview: (config) => {
      const features = [];
      if (config.businessHoursId) features.push('Hours');
      if (config.enableCallerIdMatch) features.push('CRM');
      if (config.enableRecording) features.push('Rec');
      if (config.enableZeroPressed) features.push('0-key');
      return features.length > 0 ? features.join(', ') : 'Call begins here';
    },
  },

  validation: {
    ...startWidgetCore.validation,
    validateConnections: () => ({ valid: true, errors: [] }),
  },
};
